import { emptyRichText, type TextObj } from '../../shared/types.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor } from '../editor.ts';
import { LINE_HEIGHT } from '../text/layout.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const SCREEN_FONT_PX = 24; // new text reads at this size on screen, whatever the zoom
const WIDTH_EM = 12;

/**
 * Click to place a text object and start typing. Creation and the first edit
 * form one undo step; leaving it empty discards it.
 */
export class TextTool implements Tool {
  cursor = 'text';
  private readonly ed: Editor;
  private createdId: string | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    const ed = this.ed;
    const fontSize = Math.min(400, Math.max(4, Math.round(SCREEN_FONT_PX / ed.camera.zoom)));
    const obj: TextObj = {
      id: newId(), type: 'text',
      x: e.x, y: e.y - (fontSize * LINE_HEIGHT) / 2, w: fontSize * WIDTH_EM, h: fontSize * LINE_HEIGHT,
      rotation: 0, z: ed.zKeysOnTop(1)[0]!, createdBy: ed.userId,
      fontSize, text: emptyRichText(),
    };
    ed.beginGesture();
    ed.apply([{ t: 'create', obj }]);
    this.createdId = obj.id;
  }

  onPointerMove(): void {}

  // Focus moves to the editor on pointerup; doing it on pointerdown would lose
  // it again to the browser's default mousedown focus handling.
  onPointerUp(): void {
    const id = this.createdId;
    if (!id) return;
    this.createdId = null;
    const ed = this.ed;
    ed.setTool('select');
    ed.setSelection([id]);
    ed.startTextEdit(id, { gesture: true, isNew: true, caret: 'end' });
  }

  onCancel(): void {
    if (!this.createdId) return;
    this.createdId = null;
    this.ed.cancelGesture();
  }
}
